import { useEffect, useRef } from 'react';
import { SidePanel as DesignSystemSidePanel } from '@billda/design-system';
import { usePanelStore } from './createPanelStore';
import type { SidePanelProps } from './types';

/**
 * SidePanel — portal wrapper over the DS SidePanel organism.
 *
 * @example
 * ```tsx
 * const { isOpen, type, id, closePanel } = usePanelState();
 *
 * <SidePanel open={isOpen} onClose={closePanel} title="Details" initialWidth={600}>
 *   {type === 'user' && <UserDetails id={id} />}
 *   {type === 'site' && <SiteDetails id={id} />}
 * </SidePanel>
 * ```
 *
 * Width persistence keeps the portal zustand panel store (localStorage,
 * shared width like before) and drives the DS panel as a controlled width —
 * the DS commits drag results through onWidthChange. Resize handle and
 * ESC-to-close come from the DS panel itself. `storageKey` is retained on
 * the prop signature for callers but no longer selects a CSS variable: the
 * DS panel publishes a single `--ds-side-panel-width` var.
 */
export function SidePanel({
  open,
  onClose,
  title,
  children,
  resizable = true,
  storageKey: _storageKey = 'panel',
  initialWidth,
  testId,
}: SidePanelProps) {
  const width = usePanelStore(state => state.width);
  const setWidth = usePanelStore(state => state.setWidth);
  const hasSetInitialWidth = useRef(false);

  // Set initial width when panel opens (only once)
  useEffect(() => {
    if (open && initialWidth && !hasSetInitialWidth.current) {
      setWidth(initialWidth);
      hasSetInitialWidth.current = true;
    }

    // Reset flag when panel closes
    if (!open) {
      hasSetInitialWidth.current = false;
    }
  }, [open, initialWidth, setWidth]);

  return (
    <DesignSystemSidePanel
      open={open}
      onClose={onClose}
      title={title}
      resizable={resizable}
      width={width}
      onWidthChange={setWidth}
      testId={testId}
    >
      {children}
    </DesignSystemSidePanel>
  );
}
